import { useEffect, useState, type FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LuArrowUpRight, LuArrowRight, LuCircleCheck, LuCircleHelp } from 'react-icons/lu';
import { FaWhatsapp } from 'react-icons/fa';
import FormDialog from './FormDialog';
import { SEM_SERVIDOR } from '../lib/aviso';
import { SugestoesService, TIPOS_SUGESTAO, type TipoSugestao } from '../services/SugestoesService';
import './SuporteModal.css';

/* O número do suporte é o mesmo da useBrik (decisão de 18/10/2026). A variável de
   ambiente existe para trocar sem mexer no código; sem ela, vale este. */
const WHATSAPP_SUPORTE = import.meta.env.VITE_SUPORTE_WHATSAPP || '5541995593032';
const HORARIO_SUPORTE = 'Todos os dias, das 8h às 21h';
const LIMITE_TEXTO = 2000;

interface SuporteModalProps {
    open: boolean;
    onClose: () => void;
}

const lerSessao = (): { nome?: string; empresaDescricao?: string } => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};

/**
 * "Fale com a equipe Brik": o atalho do WhatsApp em cima (o urgente) e a caixinha de
 * sugestões embaixo (o que não é urgente). O botão do rodapé envia a sugestão; o
 * WhatsApp tem o dele, para os dois não se confundirem.
 *
 * Desenho fechado em `docs/propostas/transversais/suporte-e-sugestoes.html`.
 * Clicar fora fecha (pedido de 18/10/2026). O texto digitado não se perde: o
 * estado fica aqui e volta quando a pessoa reabre.
 */
const SuporteModal: FC<SuporteModalProps> = ({ open, onClose }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [tipo, setTipo] = useState<TipoSugestao>('sugestao');
    const [texto, setTexto] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState('');
    const [enviada, setEnviada] = useState(false);

    /* Abriu de novo depois de enviar: começa limpa. Fechou no meio da escrita:
       o texto fica, para quem fechou sem querer não perder. */
    useEffect(() => {
        if (open && enviada) { setEnviada(false); setTexto(''); setTipo('sugestao'); }
        if (open) setErro('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const sessao = lerSessao();
    const mensagemWhats = `Olá! Sou ${sessao.nome || 'cliente'}`
        + (sessao.empresaDescricao ? `, da ${sessao.empresaDescricao},` : '')
        + ' e preciso de ajuda com o Brik CRM.';
    const linkWhats = `https://wa.me/${WHATSAPP_SUPORTE}?text=${encodeURIComponent(mensagemWhats)}`;

    const enviar = async () => {
        const t = texto.trim();
        if (!t) return;
        setEnviando(true);
        setErro('');
        try {
            const r = await SugestoesService.enviar({ tipo, texto: t, tela: location.pathname });
            if (r.sucesso) setEnviada(true);
            else setErro(r.mensagem || 'Não deu para enviar agora. Tente de novo em instantes.');
        } catch {
            setErro(SEM_SERVIDOR);
        } finally {
            setEnviando(false);
        }
    };

    const mandarOutra = () => { setEnviada(false); setTexto(''); setTipo('sugestao'); };

    return (
        <FormDialog
            open={open}
            onClose={onClose}
            titulo="Fale com a equipe Brik"
            sub="Fale com o suporte ou conte o que você mudaria."
            largura={480}
            rodapeTexto={enviada ? undefined : 'Urgente? Chame no WhatsApp.'}
            cancelLabel={enviada ? 'Mandar outra' : 'Cancelar'}
            onCancel={enviada ? mandarOutra : undefined}
            confirmLabel={enviada ? 'Fechar' : 'Enviar sugestão'}
            onConfirm={enviada ? onClose : enviar}
            confirmDisabled={!enviada && !texto.trim()}
            busy={enviando}
            busyLabel="Enviando…"
            fecharAoClicarFora
        >
            {enviada ? (
                <div className="sup-ok" role="status">
                    <LuCircleCheck className="sup-ok-ic" />
                    <p className="sup-ok-tit">Recebemos, obrigado!</p>
                    <p className="sup-ok-txt">
                        A equipe Brik lê todas as sugestões. Se precisarmos entender melhor, falamos com você.
                    </p>
                </div>
            ) : (
                <>
                    <div className="sup-wa">
                        <span className="sup-wa-ic" aria-hidden="true"><FaWhatsapp /></span>
                        <div className="sup-wa-txt">
                            <p className="sup-wa-tit">Suporte no WhatsApp</p>
                            <p className="sup-wa-sub">{HORARIO_SUPORTE}</p>
                        </div>
                        <a className="ds-fd-btn sup-wa-btn" href={linkWhats} target="_blank" rel="noopener noreferrer">
                            Abrir WhatsApp <LuArrowUpRight size={14} />
                        </a>
                    </div>
                    <p className="ds-fd-hint sup-nota">A conversa abre com seu nome e o da sua empresa já escritos.</p>

                    {/* A Central de ajuda morava só no menu da conta, onde quase ninguém a acha
                        (pedido de 18/10/2026). Quem abre o suporte é quem mais precisa dela. */}
                    <div className="sup-wa sup-ajuda">
                        <span className="sup-wa-ic is-ajuda" aria-hidden="true"><LuCircleHelp /></span>
                        <div className="sup-wa-txt">
                            <p className="sup-wa-tit">Central de ajuda</p>
                            <p className="sup-wa-sub">Artigos e vídeos de como usar o Brik</p>
                        </div>
                        <button type="button" className="ds-fd-btn sup-wa-btn"
                            onClick={() => { onClose(); navigate('/help'); }}>
                            Abrir ajuda <LuArrowRight size={14} />
                        </button>
                    </div>

                    <div className="sup-div"><span>ou mande uma sugestão</span></div>

                    <div className="ds-fd-campo">
                        <span className="ds-fd-lab" id="sup-tipo-lab">Sobre o quê?</span>
                        <div className="sup-chips" role="radiogroup" aria-labelledby="sup-tipo-lab">
                            {TIPOS_SUGESTAO.map(t => (
                                <button
                                    key={t.valor}
                                    type="button"
                                    role="radio"
                                    aria-checked={tipo === t.valor}
                                    className={`sup-chip ${tipo === t.valor ? 'is-on' : ''}`}
                                    onClick={() => setTipo(t.valor)}
                                >
                                    {t.rotulo}
                                </button>
                            ))}
                        </div>
                        <textarea
                            className="ds-fd-ta sup-ta"
                            aria-label="Sua sugestão"
                            placeholder="Conte o que você mudaria, o que faltou ou o que incomodou."
                            value={texto}
                            maxLength={LIMITE_TEXTO}
                            onChange={e => { setTexto(e.target.value); setErro(''); }}
                        />
                        {erro
                            ? <p className="ds-fd-hint sup-erro" role="alert">{erro}</p>
                            : <p className="ds-fd-hint">Vai para a equipe Brik com seu nome, sua empresa e a tela em que você está.</p>}
                    </div>
                </>
            )}
        </FormDialog>
    );
};

export default SuporteModal;
